import { useEffect, useState } from 'react';
import type { SemanticState, SemanticResult } from '@/semantic/useSemanticSearch';
import type { ChunkMeta } from '@/semantic/types';
import styles from './SemanticPanel.module.css';

const ACT_COLOR: Record<string, string> = { amld: '#1D4ED8', amlr: '#0D9488', amlar: '#7C3AED', tfr: '#C2410C' };
const ACT_LABEL: Record<string, string> = { amld: 'AMLD', amlr: 'AMLR', amlar: 'AMLAR', tfr: 'TFR' };
const KIND_LABEL: Record<ChunkMeta['k'], string> = {
  art: 'Article', rec: 'Recital', anx: 'Annex', def: 'Definition', mart: 'Draft article', mrec: 'Draft recital', manx: 'Draft annex',
};
const STAGE_LABEL: Record<string, string> = {
  manifest: 'Preparing', vocab: 'Vocabulary', index: 'Index', meta: 'Index', runtime: 'Runtime', model: 'Language model', session: 'Starting model',
};

export interface MandateLabel { type: string; actId: string; legalBasis: string; title: string }

interface Props {
  state: SemanticState;
  query: string;
  mandates: Record<string, MandateLabel | undefined>;
  /** Primary action for a hit (open in column / reader). */
  onOpen: (hit: SemanticResult) => void;
  /** Secondary action: open the reader in a new tab (articles, draft articles, definitions, mandates). */
  onOpenTab: (hit: SemanticResult) => void;
  onClose: () => void;
}

const mb = (n: number) => (n / 1048576).toFixed(1);

function where(meta: ChunkMeta, mandates: Props['mandates']): { act: string; ref: string; sub?: string } {
  const act = meta.a ? ACT_LABEL[meta.a] || meta.a : '';
  if (meta.m) {
    const m = mandates[meta.m];
    const head = m ? `${m.type} ${ACT_LABEL[m.actId] || ''} Art. ${m.legalBasis.replace(/^(AMLD|AMLR|AMLAR|TFR)\s*(Art\.?\s*)?/i, '')}` : meta.m;
    const ref = meta.k === 'mart' ? `Draft Art. ${meta.n}${meta.p != null && !String(meta.p).includes('.') ? `(${meta.p})` : ''}` : meta.k === 'mrec' ? `Recital ${meta.n}` : meta.t;
    return { act, ref: head, sub: ref };
  }
  if (meta.k === 'art') return { act, ref: `Art. ${meta.n}${meta.p != null && !String(meta.p).includes('.') ? `(${meta.p})` : ''}` };
  if (meta.k === 'rec') return { act, ref: `Recital ${meta.n}` };
  if (meta.k === 'anx') return { act, ref: String(meta.t).split(' — ')[0] };
  if (meta.k === 'def') return { act, ref: meta.t };
  return { act, ref: String(meta.n) };
}

export function SemanticPanel({ state, query, mandates, onOpen, onOpenTab, onClose }: Props) {
  const [hiddenFor, setHiddenFor] = useState<string | null>(null);
  useEffect(() => { setHiddenFor(null); }, [state.resultsFor]);
  // The search box re-shows hidden results (focus / Enter) via this event
  useEffect(() => {
    const onShow = () => setHiddenFor(null);
    window.addEventListener('aml:semantic-show', onShow);
    return () => window.removeEventListener('aml:semantic-show', onShow);
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const q = query.trim();
  const show = state.status === 'loading' || state.status === 'error' || (state.status === 'ready' && q.length >= 3 && hiddenFor !== state.resultsFor);
  if (!show) return null;

  const top = state.results?.[0]?.score ?? 1;

  return (
    <div className={styles.panel} role="region" aria-label="Semantic search results" data-tour="semantic-panel">
      <div className={styles.head}>
        {state.status === 'loading' && (
          <div className={styles.loading}>
            <span className={styles.spinner} aria-hidden />
            <span>
              Setting up search by meaning — one-time download, then cached by your browser.
              {state.progress && <> <strong>{STAGE_LABEL[state.progress.stage] || state.progress.stage}</strong>{state.progress.total > 1 ? ` ${mb(state.progress.loaded)} / ${mb(state.progress.total)} MB` : '…'}</>}
            </span>
            {state.progress && state.progress.total > 1 && (
              <span className={styles.bar}><span className={styles.barFill} style={{ width: `${Math.min(100, (100 * state.progress.loaded) / state.progress.total)}%` }} /></span>
            )}
          </div>
        )}
        {state.status === 'error' && (
          <div className={styles.error}>Search by meaning is unavailable in this browser ({state.error}). Keyword search still works — switch the toggle off.</div>
        )}
        {state.status === 'ready' && (
          <div className={styles.summary}>
            {state.searching || state.resultsFor !== q ? <span>Searching…</span> : state.results && state.results.length > 0
              ? <span><strong>{state.results.length}</strong> closest passages for <em>“{state.resultsFor}”</em>{state.info ? <span className={styles.dim}> · {state.info.chunks.toLocaleString('en-GB')} passages indexed{state.lastMs != null ? ` · ${state.lastMs} ms` : ''}</span> : null}</span>
              : <span>No passages found.</span>}
            <span className={styles.hint}>click → open · ↗ new tab · Esc close</span>
          </div>
        )}
        <button className={styles.close} onClick={() => { setHiddenFor(state.resultsFor); onClose(); }} aria-label="Close results">×</button>
      </div>

      {state.status === 'ready' && state.results && state.results.length > 0 && state.resultsFor === q && (
        <ol className={styles.list}>
          {state.results.map((hit, idx) => {
            const w = where(hit.meta, mandates);
            const color = hit.meta.a ? ACT_COLOR[hit.meta.a] : '#64748b';
            const rel = top > 0 ? Math.max(0.08, hit.score / top) : 0;
            const tabbable = hit.meta.k === 'art' || hit.meta.k === 'def' || !!hit.meta.m;
            return (
              <li key={`${hit.i}`} className={styles.item} style={{ borderLeftColor: color }}>
                <button className={styles.itemMain} onClick={() => { onOpen(hit); if (hit.meta.k === 'art') setHiddenFor(state.resultsFor); }} title={hit.meta.k === 'art' ? 'Open in the column (new tab if the act is hidden) — the results hide; click the search box to bring them back' : 'Open'}>
                  <span className={styles.itemHead}>
                    <span className={styles.rank}>{idx + 1}</span>
                    <span className={styles.kind}>{KIND_LABEL[hit.meta.k]}</span>
                    <span className={styles.act} style={{ color }}>{w.act}</span>
                    <span className={styles.ref}>{w.ref}</span>
                    {w.sub && <span className={styles.sub}>{w.sub}</span>}
                    {hit.meta.k !== 'rec' && hit.meta.k !== 'mrec' && hit.meta.k !== 'anx' && hit.meta.k !== 'def' && <span className={styles.title}>— {hit.meta.t}</span>}
                    <span className={styles.match} title={`similarity ${hit.score.toFixed(3)}`}><span className={styles.matchFill} style={{ width: `${Math.round(rel * 100)}%`, background: color }} /></span>
                  </span>
                  <span className={styles.snippet}>{hit.meta.s}</span>
                </button>
                {tabbable && <button className={styles.tab} onClick={() => onOpenTab(hit)} title="Open the reader in a new tab" aria-label="Open in a new tab">↗</button>}
              </li>
            );
          })}
        </ol>
      )}

      {state.status === 'ready' && (
        <div className={styles.foot}>
          Ranked by meaning with a small language model that runs inside your browser{state.info ? ` (${state.info.model.split('/').pop()})` : ''} — nothing leaves your device. Passages are verbatim; the ranking is approximate: verify in the source text.
        </div>
      )}
    </div>
  );
}
